import axios from 'axios';
import { environment } from '../environments/environment';

const apiEndpoint = environment.webservices.endpoint;
const JSON_HEADERS = {
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
};

/** A KB experiment template + the readouts it prepopulates. */
export interface ExperimentTemplateReadout {
  measurement_name: string | null;
  questionnaire_name: string | null;
  /** KB-sourced patient-facing label ("LDL-C", "creatine kinase, serum"). */
  display_name?: string | null;
  role: string;
  rank: string | null;
  expectation: string | null;
  default_recheck_days: number | null;
  declared_by: string | null;
}

export interface ExperimentTemplate {
  name: string;
  display_name: string | null;
  change: string;
  perturbation_class: string | null;
  perturbation_ingredient: string | null;
  programs: string[];
  description: string | null;
  readouts: ExperimentTemplateReadout[];
}

export interface ExperimentReadout {
  measurement_name: string | null;
  questionnaire_name: string | null;
  /** KB-sourced patient-facing label ("LDL-C", "creatine kinase, serum"). */
  display_name?: string | null;
  role: string;
  rank: string | null;
  expected_direction: string | null;
  finding_direction: string | null;
  expected_low: number | null;
  expected_high: number | null;
  expected_unit: string | null;
  magnitude_asserted: boolean;
  recheck_at: number | null;
  source: string;
}

export interface Experiment {
  id: number;
  name: string;
  template_name: string | null;
  change: string;
  status: string;
  encounter_date: number;
  medication_id: number | null;
  perturbation_label: string | null;
  hypotheses: unknown[];
  disposition_clinician: string | null;
  disposition_patient: string | null;
  readouts: ExperimentReadout[];
}

/** Body for authoring an experiment. Only add/subtract/override the template. */
export interface CreateExperimentPayload {
  template_name: string;
  name?: string;
  change?: string;
  medication_id?: number | null;
  add_readouts?: { measurement_name?: string; questionnaire_name?: string; rank?: string }[];
  remove_subjects?: string[];
  readout_overrides?: Record<string, unknown>;
  hypotheses?: unknown[];
  disposition_clinician?: string;
  disposition_patient?: string;
}

/**
 * Provider-facing experiment authoring. Reads templates + a patient's experiments,
 * creates one from a template with add/subtract/override. Auth is the global axios
 * interceptor (clinician Okta); check_patient_status resolves the ref_id server-side.
 */
/** A KB symptom concept + the toxicity questionnaire it resolves to. */
export interface SymptomConcept {
  name: string;
  display_name: string;
  questionnaire_name: string;
  has_questionnaire: boolean;
  body_system?: string;
}

export class ExperimentService {
  async getTemplates(): Promise<ExperimentTemplate[]> {
    const res = await axios.get<{ data: ExperimentTemplate[] }>(
      `${apiEndpoint}/experiment-templates`,
    );
    return res.data.data;
  }

  async getExperiments(refId: string): Promise<Experiment[]> {
    const res = await axios.get<{ data: Experiment[] }>(
      `${apiEndpoint}/patients/${encodeURIComponent(refId)}/experiments`,
    );
    return res.data.data;
  }

  async createExperiment(refId: string, payload: CreateExperimentPayload): Promise<Experiment> {
    const res = await axios.post<{ data: Experiment }>(
      `${apiEndpoint}/patients/${encodeURIComponent(refId)}/experiments`,
      payload,
      JSON_HEADERS,
    );
    return res.data.data;
  }

  async deleteExperiment(refId: string, id: number): Promise<void> {
    await axios.delete(
      `${apiEndpoint}/patients/${encodeURIComponent(refId)}/experiments/${id}`,
    );
  }

  /**
   * Move a plan along its lifecycle, KEEPING the record (ADR D17).
   *
   * 'active'   ACTIVATE a proposed plan — it starts driving the patient's symptom
   *            check-ins as well as the charts (ADR D19).
   * 'closed'   SHUT OFF a running plan that has been read out — its symptoms drop
   *            off both surfaces; readouts and history are kept.
   * 'canceled' abandon a plan (from proposed or active).
   *
   * Illegal moves answer 409 — the server owns the rule, this never second-guesses
   * it. Prefer this over deleteExperiment for anything that ran: delete destroys the
   * record, this retires it.
   */
  async transitionStatus(
    refId: string,
    id: number,
    status: 'active' | 'closed' | 'canceled',
  ): Promise<Experiment> {
    const res = await axios.put<{ data: Experiment }>(
      `${apiEndpoint}/patients/${encodeURIComponent(refId)}/experiments/${id}/status`,
      { status },
      JSON_HEADERS,
    );
    return res.data.data;
  }

  /** Add/remove readouts on an existing experiment (edit-later). */
  async updateReadouts(
    refId: string,
    id: number,
    payload: {
      add_readouts?: { measurement_name?: string; questionnaire_name?: string }[];
      remove_subjects?: string[];
    },
  ): Promise<Experiment> {
    const res = await axios.put<{ data: Experiment }>(
      `${apiEndpoint}/patients/${encodeURIComponent(refId)}/experiments/${id}/readouts`,
      payload,
      JSON_HEADERS,
    );
    return res.data.data;
  }

  /** Search the symptom-concept namespace (server-side) for questionnaire readouts. */
  async searchSymptomConcepts(term: string): Promise<SymptomConcept[]> {
    const res = await axios.get<SymptomConcept[]>(
      `${apiEndpoint}/symptom-concepts`,
      { params: { name: term, limit: 30 } },
    );
    return res.data;
  }
}

export const experimentService = new ExperimentService();
